import React from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Menu } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export function Wordmark({ className }: { className?: string }) {
  return (
    <Link
      to="/"
      className={cn(
        'shrink-0 text-xl font-extrabold tracking-tight text-brand transition-opacity hover:opacity-80',
        className
      )}
    >
      lectureme
    </Link>
  );
}

interface AppHeaderProps {
  /** Rendered next to the wordmark on the left. */
  left?: React.ReactNode;
  /** Right-aligned controls (status, actions, user menu). */
  right?: React.ReactNode;
  className?: string;
}

export function AppHeader({ left, right, className }: AppHeaderProps) {
  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80',
        className
      )}
    >
      <div className="container flex h-16 items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <Wordmark />
          {left}
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">{right}</div>
      </div>
    </header>
  );
}

function initialsFor(label: string): string {
  const parts = label.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '');
  return letters.join('') || '?';
}

interface UserMenuProps {
  name: string;
  email?: string;
  onLogout: () => void;
  children?: React.ReactNode;
}

/** Airbnb-style pill trigger: hamburger + avatar, opening a small account menu. */
export function UserMenu({ name, email, onLogout, children }: UserMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="flex h-10 items-center gap-2 rounded-full border bg-background pl-1.5 pr-1.5 transition-shadow hover:shadow-soft sm:pl-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Menu className="hidden h-4 w-4 text-muted-foreground sm:block" aria-hidden />
          <Avatar className="h-7 w-7">
            <AvatarFallback className="bg-foreground text-[11px] font-semibold text-background">
              {initialsFor(name)}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 rounded-xl p-1.5">
        <DropdownMenuLabel className="px-2 py-1.5">
          <div className="truncate text-sm font-semibold">{name}</div>
          {email && email !== name && (
            <div className="truncate text-xs font-normal text-muted-foreground">{email}</div>
          )}
        </DropdownMenuLabel>
        {children && (
          <>
            <DropdownMenuSeparator />
            {children}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout} className="rounded-md">
          <LogOut className="h-4 w-4" aria-hidden />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ConnectionDot({ connected }: { connected: boolean }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground">
      <span className="relative flex h-2 w-2">
        {connected && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
        )}
        <span
          className={cn(
            'relative inline-flex h-2 w-2 rounded-full',
            connected ? 'bg-success' : 'bg-muted-foreground'
          )}
        />
      </span>
      {connected ? 'Live' : 'Reconnecting'}
    </span>
  );
}
